"use client"

import { useState, useContext, useEffect } from "react"
import { ErpContext } from "../../context/ErpContext.js"
import { TextField, Button, Typography, Container, Grid, Box, Card, CardContent, Chip, Alert, IconButton, Divider, CircularProgress, Badge, Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText, FormControlLabel, Checkbox } from "@mui/material"
import { LocationOn, Category, Add, Settings, Save, Image as ImageIcon, Cancel, CheckCircle, Edit, Delete } from "@mui/icons-material"

const IMAGE_PARAM_NAMES = new Set(["logoTenant", "logoNavbarTenant"])

const GestionUbicacionComponent = () => {
  const [alertUbicacion, setAlertUbicacion] = useState(false)
  const [alertTipoEntidad, setAlertTipoEntidad] = useState(false)
  const [alertParametros, setAlertParametros] = useState({ show: false, message: "", severity: "success" })

  /* PARAMETROS GLOBALES */
  const { getParametrosGlobalesMenu, updateParametroGlobal } = useContext(ErpContext)
  const [parametrosGlobales, setParametrosGlobales] = useState([])
  const [parametrosValues, setParametrosValues] = useState({})
  const [parametrosOriginales, setParametrosOriginales] = useState({})
  const [loadingParametros, setLoadingParametros] = useState(false)
  const [savingParametros, setSavingParametros] = useState(false)

  /* UBICACION */
  const { ubicaciones, getUbicacion, postUbicacion, updateUbicacion, deleteUbicacion, isNavbarCollapsed } = useContext(ErpContext)
  const [descripcionUbicacion, setDescripcionUbicacion] = useState("")
  const [esDestinoUbicacion, setEsDestinoUbicacion] = useState(true)

  useEffect(() => {
    getUbicacion()
  }, [])

  const handleUbicacionSubmit = (e) => {
    e.preventDefault()
    postUbicacion(descripcionUbicacion, esDestinoUbicacion)
    setDescripcionUbicacion("")
    setEsDestinoUbicacion(true)
    setAlertUbicacion(true)
    setTimeout(() => setAlertUbicacion(false), 3000)
  }

  /* TIPO ENTIDAD */
  const { tipoEntidades, getTipoEntidad, postTipoEntidad, updateTipoEntidad, deleteTipoEntidad } = useContext(ErpContext)
  const [descripcionTipoEntidad, setDescripcionTipoEntidad] = useState("")
  
  // Estados para modales de edición
  const [editUbicacion, setEditUbicacion] = useState({ open: false, id: null, descripcion: "", esDestino: true })
  const [editTipoEntidad, setEditTipoEntidad] = useState({ open: false, id: null, descripcion: "" })
  
  // Estados para diálogos de confirmación de eliminación
  const [deleteConfirmUbicacion, setDeleteConfirmUbicacion] = useState({ open: false, id: null, descripcion: "" })
  const [deleteConfirmTipoEntidad, setDeleteConfirmTipoEntidad] = useState({ open: false, id: null, descripcion: "" })

  useEffect(() => {
    getTipoEntidad()
  }, [])

  const handleTipoEntidadSubmit = (e) => {
    e.preventDefault()
    postTipoEntidad(descripcionTipoEntidad)
    setDescripcionTipoEntidad("")
    setAlertTipoEntidad(true)
    setTimeout(() => setAlertTipoEntidad(false), 3000)
  }

  /* CARGAR PARAMETROS GLOBALES */
  useEffect(() => {
    loadParametrosGlobales()
  }, [])

  const loadParametrosGlobales = async () => {
    try {
      setLoadingParametros(true)
      const data = await getParametrosGlobalesMenu()
      setParametrosGlobales(data)
      
      // Inicializar valores
      const initialValues = {}
      data.forEach(param => {
        initialValues[param.nombreParametro] = param.valorParametro || ""
      })
      setParametrosValues(initialValues)
      setParametrosOriginales(initialValues)
    } catch (error) {
      console.error("Error cargando parámetros globales:", error)
      setAlertParametros({ 
        show: true, 
        message: "Error al cargar parámetros de configuración", 
        severity: "error" 
      })
      setTimeout(() => setAlertParametros({ show: false, message: "", severity: "success" }), 3000)
    } finally {
      setLoadingParametros(false)
    }
  }

  const handleParametroChange = (nombreParametro, value) => {
    setParametrosValues(prev => ({
      ...prev,
      [nombreParametro]: value
    }))
  }

  const handleSaveAllParametros = async () => {
    try {
      setSavingParametros(true)
      
      // Identificar parámetros modificados
      const parametrosModificados = Object.keys(parametrosValues).filter(
        nombreParametro => parametrosValues[nombreParametro] !== parametrosOriginales[nombreParametro]
      )

      if (parametrosModificados.length === 0) {
        setAlertParametros({ 
          show: true, 
          message: "No hay cambios para guardar", 
          severity: "info" 
        })
        setTimeout(() => setAlertParametros({ show: false, message: "", severity: "success" }), 3000)
        return
      }

      // Guardar todos los parámetros modificados
      for (const nombreParametro of parametrosModificados) {
        const valorParametro = parametrosValues[nombreParametro]
        await updateParametroGlobal(nombreParametro, valorParametro)
      }
      
      setAlertParametros({ 
        show: true, 
        message: `${parametrosModificados.length} parámetro${parametrosModificados.length > 1 ? 's' : ''} actualizado${parametrosModificados.length > 1 ? 's' : ''} exitosamente`, 
        severity: "success" 
      })
      setTimeout(() => setAlertParametros({ show: false, message: "", severity: "success" }), 3000)
      
      // Recargar parámetros
      await loadParametrosGlobales()
    } catch (error) {
      console.error("Error guardando parámetros:", error)
      setAlertParametros({ 
        show: true, 
        message: "Error al guardar los parámetros", 
        severity: "error" 
      })
      setTimeout(() => setAlertParametros({ show: false, message: "", severity: "success" }), 3000)
    } finally {
      setSavingParametros(false)
    }
  }

  const handleCancelarCambios = () => {
    setParametrosValues({ ...parametrosOriginales })
    setAlertParametros({ 
      show: true, 
      message: "Cambios descartados", 
      severity: "info" 
    })
    setTimeout(() => setAlertParametros({ show: false, message: "", severity: "success" }), 3000)
  }

  const hayParametrosModificados = () => {
    return Object.keys(parametrosValues).some(
      nombreParametro => parametrosValues[nombreParametro] !== parametrosOriginales[nombreParametro]
    )
  }

  const handleImageChange = async (e, nombreParametro) => {
    const file = e.target.files[0]
    if (file) {
      try {
        // Validar que sea una imagen
        if (!file.type.startsWith('image/')) {
          setAlertParametros({ 
            show: true, 
            message: "Por favor seleccione un archivo de imagen", 
            severity: "error" 
          })
          setTimeout(() => setAlertParametros({ show: false, message: "", severity: "success" }), 3000)
          return
        }

        // Convertir a base64
        const reader = new FileReader()
        reader.onloadend = () => {
          const base64String = reader.result
          handleParametroChange(nombreParametro, base64String)
        }
        reader.readAsDataURL(file)
      } catch (error) {
        console.error("Error procesando imagen:", error)
        setAlertParametros({ 
          show: true, 
          message: "Error al procesar la imagen", 
          severity: "error" 
        })
        setTimeout(() => setAlertParametros({ show: false, message: "", severity: "success" }), 3000)
      }
    }
  }

  // Funciones para manejar edición de Ubicación
  const handleEditUbicacion = (ubicacion) => {
    setEditUbicacion({ 
      open: true, 
      id: ubicacion.id, 
      descripcion: ubicacion.descripcion,
      esDestino: ubicacion.esDestino !== null ? ubicacion.esDestino : true
    })
  }

  const handleSaveEditUbicacion = async () => {
    try {
      await updateUbicacion(editUbicacion.id, editUbicacion.descripcion, editUbicacion.esDestino)
      setEditUbicacion({ open: false, id: null, descripcion: "", esDestino: true })
      setAlertUbicacion(true)
      setTimeout(() => setAlertUbicacion(false), 3000)
    } catch (error) {
      console.error("Error al actualizar ubicación:", error)
    }
  }

  // Funciones para manejar eliminación de Ubicación
  const handleDeleteUbicacion = (ubicacion) => {
    setDeleteConfirmUbicacion({ open: true, id: ubicacion.id, descripcion: ubicacion.descripcion })
  }

  const handleConfirmDeleteUbicacion = async () => {
    try {
      await deleteUbicacion(deleteConfirmUbicacion.id)
      setDeleteConfirmUbicacion({ open: false, id: null, descripcion: "" })
      setAlertUbicacion(true)
      setTimeout(() => setAlertUbicacion(false), 3000)
    } catch (error) {
      console.error("Error al eliminar ubicación:", error)
    }
  }

  // Funciones para manejar edición de TipoEntidad
  const handleEditTipoEntidad = (tipoEntidad) => {
    setEditTipoEntidad({ open: true, id: tipoEntidad.id, descripcion: tipoEntidad.descripcion })
  }

  const handleSaveEditTipoEntidad = async () => {
    try {
      await updateTipoEntidad(editTipoEntidad.id, editTipoEntidad.descripcion)
      setEditTipoEntidad({ open: false, id: null, descripcion: "" })
      setAlertTipoEntidad(true)
      setTimeout(() => setAlertTipoEntidad(false), 3000)
    } catch (error) {
      console.error("Error al actualizar tipo de entidad:", error)
    }
  }

  // Funciones para manejar eliminación de TipoEntidad
  const handleDeleteTipoEntidad = (tipoEntidad) => {
    setDeleteConfirmTipoEntidad({ open: true, id: tipoEntidad.id, descripcion: tipoEntidad.descripcion })
  }

  const handleConfirmDeleteTipoEntidad = async () => {
    try {
      await deleteTipoEntidad(deleteConfirmTipoEntidad.id)
      setDeleteConfirmTipoEntidad({ open: false, id: null, descripcion: "" })
      setAlertTipoEntidad(true)
      setTimeout(() => setAlertTipoEntidad(false), 3000)
    } catch (error) {
      console.error("Error al eliminar tipo de entidad:", error)
    }
  }

  return (
    <Container
      maxWidth="xl"
      sx={{
        py: 3,
      }}
    >
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 600,
            color: "#5b5b5f",
            mb: 1,
          }}
        >
          Gestión de Configuraciones
        </Typography>
        <Typography variant="body1" sx={{ color: "#666" }}>
          Administra ubicaciones y tipos de entidad del sistema
        </Typography>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} lg={6}>
          <Card
            elevation={0}
            sx={{
              border: "1px solid #e0e0e0",
              borderRadius: 2,
              height: "100%",
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                <Box
                  sx={{
                    backgroundColor: "rgba(244, 177, 14, 0.1)",
                    borderRadius: "50%",
                    p: 1,
                    mr: 2,
                  }}
                >
                  <LocationOn sx={{ color: "rgb(244, 177, 14)", fontSize: 24 }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 600, color: "#5b5b5f" }}>
                  Ubicaciones
                </Typography>
              </Box>

              {alertUbicacion && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  Ubicación agregada exitosamente
                </Alert>
              )}

              <form onSubmit={handleUbicacionSubmit}>
                <TextField
                  label="Nueva Ubicación"
                  variant="outlined"
                  fullWidth
                  value={descripcionUbicacion}
                  onChange={(e) => setDescripcionUbicacion(e.target.value)}
                  sx={{ mb: 1 }}
                  size="small"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={esDestinoUbicacion}
                      onChange={(e) => setEsDestinoUbicacion(e.target.checked)}
                      sx={{
                        color: "rgb(244, 177, 14)",
                        "&.Mui-checked": {
                          color: "rgb(244, 177, 14)",
                        },
                      }}
                    />
                  }
                  label="Es Destino"
                  sx={{ mb: 2 }}
                />
                <Button
                  variant="contained"
                  type="submit"
                  fullWidth
                  startIcon={<Add />}
                  sx={{
                    backgroundColor: "#5b5b5f",
                    "&:hover": { backgroundColor: "#4a4a4e" },
                    mb: 3,
                    textTransform: "none",
                    fontWeight: 500,
                  }}
                >
                  Agregar Ubicación
                </Button>
              </form>

              <Box>
                <Typography variant="subtitle2" sx={{ mb: 2, color: "#666" }}>
                  Ubicaciones Existentes ({ubicaciones.length})
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {ubicaciones.map((ubicacion) => (
                    <Box
                      key={ubicacion.id}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        border: "1px solid rgb(244, 177, 14)",
                        borderRadius: 1,
                        padding: "4px 8px",
                        backgroundColor: "rgba(244, 177, 14, 0.05)",
                      }}
                    >
                      <Typography variant="body2" sx={{ color: "rgb(244, 177, 14)" }}>
                        {ubicacion.descripcion}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => handleEditUbicacion(ubicacion)}
                        sx={{
                          padding: "2px",
                          color: "rgb(244, 177, 14)",
                          "&:hover": { backgroundColor: "rgba(244, 177, 14, 0.1)" },
                        }}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteUbicacion(ubicacion)}
                        sx={{
                          padding: "2px",
                          color: "#d32f2f",
                          "&:hover": { backgroundColor: "rgba(211, 47, 47, 0.1)" },
                        }}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} lg={6}>
          <Card
            elevation={0}
            sx={{
              border: "1px solid #e0e0e0",
              borderRadius: 2,
              height: "100%",
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                <Box
                  sx={{
                    backgroundColor: "rgba(244, 177, 14, 0.1)",
                    borderRadius: "50%",
                    p: 1,
                    mr: 2,
                  }}
                >
                  <Category sx={{ color: "rgb(244, 177, 14)", fontSize: 24 }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 600, color: "#5b5b5f" }}>
                  Tipos de Entidad
                </Typography>
              </Box>

              {alertTipoEntidad && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  Tipo de entidad agregado exitosamente
                </Alert>
              )}

              <form onSubmit={handleTipoEntidadSubmit}>
                <TextField
                  label="Nuevo Tipo de Entidad"
                  variant="outlined"
                  fullWidth
                  value={descripcionTipoEntidad}
                  onChange={(e) => setDescripcionTipoEntidad(e.target.value)}
                  sx={{ mb: 2 }}
                  size="small"
                />
                <Button
                  variant="contained"
                  type="submit"
                  fullWidth
                  startIcon={<Add />}
                  sx={{
                    backgroundColor: "#5b5b5f",
                    "&:hover": { backgroundColor: "#4a4a4e" },
                    mb: 3,
                    textTransform: "none",
                    fontWeight: 500,
                  }}
                >
                  Agregar Tipo
                </Button>
              </form>

              <Box>
                <Typography variant="subtitle2" sx={{ mb: 2, color: "#666" }}>
                  Tipos Existentes ({tipoEntidades.length})
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {tipoEntidades.map((tipoEntidad) => (
                    <Box
                      key={tipoEntidad.id}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        border: "1px solid rgb(244, 177, 14)",
                        borderRadius: 1,
                        padding: "4px 8px",
                        backgroundColor: "rgba(244, 177, 14, 0.05)",
                      }}
                    >
                      <Typography variant="body2" sx={{ color: "rgb(244, 177, 14)" }}>
                        {tipoEntidad.descripcion}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => handleEditTipoEntidad(tipoEntidad)}
                        sx={{
                          padding: "2px",
                          color: "rgb(244, 177, 14)",
                          "&:hover": { backgroundColor: "rgba(244, 177, 14, 0.1)" },
                        }}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteTipoEntidad(tipoEntidad)}
                        sx={{
                          padding: "2px",
                          color: "#d32f2f",
                          "&:hover": { backgroundColor: "rgba(211, 47, 47, 0.1)" },
                        }}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* SECCION DE PARAMETROS GLOBALES */}
      <Box sx={{ mt: 4 }}>
        <Divider sx={{ mb: 3 }} />
        
        <Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 2 }}>
          <Box
            sx={{
              backgroundColor: "rgba(244, 177, 14, 0.1)",
              borderRadius: "50%",
              p: 1.5,
            }}
          >
            <Settings sx={{ color: "rgb(244, 177, 14)", fontSize: 28 }} />
          </Box>
          <Box>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 600,
                color: "#5b5b5f",
              }}
            >
              Parámetros Globales
            </Typography>
            <Typography variant="body2" sx={{ color: "#666" }}>
              Configura los parámetros del sistema
            </Typography>
          </Box>
        </Box>

        {alertParametros.show && (
          <Alert severity={alertParametros.severity} sx={{ mb: 3 }}>
            {alertParametros.message}
          </Alert>
        )}

        {loadingParametros ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={3}>
            {parametrosGlobales.map((parametro) => (
              <Grid item xs={12} md={6} key={parametro.nombreParametro}>
                <Card
                  elevation={0}
                  sx={{
                    border: "1px solid #e0e0e0",
                    borderRadius: 2,
                    height: "100%",
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Typography 
                      variant="subtitle1" 
                      sx={{ fontWeight: 600, color: "#5b5b5f", mb: 2 }}
                    >
                      {parametro.descripcion}
                    </Typography>

                    {IMAGE_PARAM_NAMES.has(parametro.nombreParametro) ? (
                      <Box>
                        <input
                          accept="image/*"
                          style={{ display: "none" }}
                          id={`file-input-${parametro.nombreParametro}`}
                          type="file"
                          onChange={(e) => handleImageChange(e, parametro.nombreParametro)}
                        />
                        <label htmlFor={`file-input-${parametro.nombreParametro}`}>
                          <Button
                            variant="outlined"
                            component="span"
                            startIcon={<ImageIcon />}
                            fullWidth
                            sx={{
                              borderColor: "rgb(244, 177, 14)",
                              color: "rgb(244, 177, 14)",
                              "&:hover": {
                                borderColor: "rgb(244, 177, 14)",
                                backgroundColor: "rgba(244, 177, 14, 0.05)",
                              },
                            }}
                          >
                            Seleccionar Imagen
                          </Button>
                        </label>
                        
                        {parametrosValues[parametro.nombreParametro] && (
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              p: 2,
                              border: "1px solid #e0e0e0",
                              borderRadius: 2,
                              mt: 2,
                            }}
                          >
                            <img
                              src={parametrosValues[parametro.nombreParametro]}
                              alt="Logo preview"
                              style={{
                                maxWidth: "200px",
                                maxHeight: "100px",
                                objectFit: "contain",
                              }}
                            />
                          </Box>
                        )}
                      </Box>
                    ) : (
                      <TextField
                        fullWidth
                        variant="outlined"
                        size="small"
                        multiline
                        rows={2}
                        value={parametrosValues[parametro.nombreParametro] || ""}
                        onChange={(e) => handleParametroChange(parametro.nombreParametro, e.target.value)}
                        placeholder="Ingrese el valor"
                      />
                    )}

                    <Typography
                      variant="caption"
                      sx={{ display: "block", mt: 2, color: "#999", fontStyle: "italic" }}
                    >
                      {parametro.nombreParametro}
                    </Typography>
                    
                    {parametrosValues[parametro.nombreParametro] !== parametrosOriginales[parametro.nombreParametro] && (
                      <Chip
                        label="Modificado"
                        size="small"
                        color="warning"
                        sx={{ mt: 1 }}
                      />
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {/* BOTONES DE ACCIÓN */}
        {!loadingParametros && parametrosGlobales.length > 0 && (
          <Box sx={{ mt: 4, display: "flex", gap: 2, justifyContent: "flex-end" }}>
            <Button
              variant="outlined"
              startIcon={<Cancel />}
              onClick={handleCancelarCambios}
              disabled={savingParametros || !hayParametrosModificados()}
              sx={{
                borderColor: "#999",
                color: "#999",
                "&:hover": {
                  borderColor: "#666",
                  backgroundColor: "rgba(0,0,0,0.05)",
                },
                textTransform: "none",
                fontWeight: 500,
              }}
            >
              Descartar Cambios
            </Button>
            
            <Button
              variant="contained"
              startIcon={
                savingParametros ? (
                  <CircularProgress size={20} sx={{ color: "white" }} />
                ) : (
                  <Badge 
                    badgeContent={Object.keys(parametrosValues).filter(
                      key => parametrosValues[key] !== parametrosOriginales[key]
                    ).length} 
                    color="error"
                    invisible={!hayParametrosModificados()}
                  >
                    <CheckCircle />
                  </Badge>
                )
              }
              onClick={handleSaveAllParametros}
              disabled={savingParametros || !hayParametrosModificados()}
              sx={{
                backgroundColor: "rgb(244, 177, 14)",
                "&:hover": { backgroundColor: "rgb(220, 160, 10)" },
                "&:disabled": { 
                  backgroundColor: "#e0e0e0",
                  color: "#999"
                },
                textTransform: "none",
                fontWeight: 600,
                px: 4,
              }}
            >
              {savingParametros ? "Guardando..." : "Guardar Cambios"}
            </Button>
          </Box>
        )}
      </Box>

      {/* Modales de edición y diálogos de confirmación */}
      
      {/* Modal editar Ubicación */}
      <Dialog open={editUbicacion.open} onClose={() => setEditUbicacion({ open: false, id: null, descripcion: "", esDestino: true })}>
        <DialogTitle>Editar Ubicación</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            label="ID de ubicación"
            fullWidth
            variant="outlined"
            value={editUbicacion.id ?? ""}
            disabled
            helperText="Usa este valor en Parámetros Globales para referenciar esta ubicación"
            sx={{ mb: 1 }}
          />
          <TextField
            autoFocus
            margin="dense"
            label="Descripción"
            fullWidth
            variant="outlined"
            value={editUbicacion.descripcion}
            onChange={(e) => setEditUbicacion({ ...editUbicacion, descripcion: e.target.value })}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={editUbicacion.esDestino}
                onChange={(e) => setEditUbicacion({ ...editUbicacion, esDestino: e.target.checked })}
                sx={{
                  color: "rgb(244, 177, 14)",
                  "&.Mui-checked": {
                    color: "rgb(244, 177, 14)",
                  },
                }}
              />
            }
            label="Es Destino"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditUbicacion({ open: false, id: null, descripcion: "", esDestino: true })}>Cancelar</Button>
          <Button onClick={handleSaveEditUbicacion} variant="contained" disabled={!editUbicacion.descripcion.trim()}>
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo confirmar eliminar Ubicación */}
      <Dialog open={deleteConfirmUbicacion.open} onClose={() => setDeleteConfirmUbicacion({ open: false, id: null, descripcion: "" })}>
        <DialogTitle>Eliminar Ubicación</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Estás seguro de que deseas eliminar la ubicación <strong>{deleteConfirmUbicacion.descripcion}</strong>?
            Esta acción no se puede deshacer.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmUbicacion({ open: false, id: null, descripcion: "" })}>Cancelar</Button>
          <Button onClick={handleConfirmDeleteUbicacion} variant="contained" color="error">
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal editar TipoEntidad */}
      <Dialog open={editTipoEntidad.open} onClose={() => setEditTipoEntidad({ open: false, id: null, descripcion: "" })}>
        <DialogTitle>Editar Tipo de Entidad</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Descripción"
            fullWidth
            variant="outlined"
            value={editTipoEntidad.descripcion}
            onChange={(e) => setEditTipoEntidad({ ...editTipoEntidad, descripcion: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditTipoEntidad({ open: false, id: null, descripcion: "" })}>Cancelar</Button>
          <Button onClick={handleSaveEditTipoEntidad} variant="contained" disabled={!editTipoEntidad.descripcion.trim()}>
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo confirmar eliminar TipoEntidad */}
      <Dialog open={deleteConfirmTipoEntidad.open} onClose={() => setDeleteConfirmTipoEntidad({ open: false, id: null, descripcion: "" })}>
        <DialogTitle>Eliminar Tipo de Entidad</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Estás seguro de que deseas eliminar el tipo de entidad <strong>{deleteConfirmTipoEntidad.descripcion}</strong>?
            Esta acción no se puede deshacer.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmTipoEntidad({ open: false, id: null, descripcion: "" })}>Cancelar</Button>
          <Button onClick={handleConfirmDeleteTipoEntidad} variant="contained" color="error">
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  )
}

export default GestionUbicacionComponent
